/**
 * Shared utilities for content-pool chat components
 */
import React from 'react'

export class ChatUtils {
  /**
   * Generate appropriate success message based on action type and item count
   */
  static getSuccessMessage(itemIds: string | string[], actionType: 'single' | 'bulk', itemType: 'article' | 'source'): string {
    const itemCount = Array.isArray(itemIds) ? itemIds.length : itemIds.toString().split(',').length
    const itemLabel = itemType === 'article' ? 'article' : 'source'
    const itemLabelPlural = itemType === 'article' ? 'articles' : 'sources'
    
    if (actionType === 'bulk' || itemCount > 1) {
      return `🎉 Successfully added ${itemCount} ${itemLabelPlural} to your upcoming content pool!`
    } else {
      return `✅ ${itemLabel.charAt(0).toUpperCase() + itemLabel.slice(1)} has been successfully added to your upcoming content pool.`
    }
  }

  /**
   * Render user avatar with fallback
   */
  static renderUserAvatar(person: any): JSX.Element {
    // For now, just return the default icon to avoid JSX compilation issues
    // In the future, this could be enhanced to show actual user profile images
    return React.createElement('i', { className: 'fas fa-user' })
  }

  /**
   * Capitalize words in a string
   */
  static capitalizeWords(str: string): string {
    return str.replace(/\b\w+/g, word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    )
  }

  /**
   * Truncate text with ellipsis
   */
  static truncateText(text: string, maxLength: number): string {
    return text.length > maxLength 
      ? `${text.substring(0, maxLength)}...`
      : text
  }

  /**
   * Generate clean chat title from generic prompts
   */
  static getCleanChatTitle(title: string, contentType: 'article' | 'source' = 'article'): string {
    const contentLabel = contentType === 'article' ? 'Content' : 'Sources'
    
    // Clean up generic prompts for better UX
    if (title.startsWith("Suggest articles that are relevant to my newsletter about")) {
      // Try to extract the topic from the end of the prompt
      const match = title.match(/about\s+(.+)\.?$/)
      if (match && match[1] && match[1].trim().length > 3) {
        const topic = match[1].trim()
        return `${ChatUtils.capitalizeWords(topic)} ${contentLabel}`
      }
      return `Newsletter ${contentLabel} Discovery`
    }
    
    if (title.includes("suggest sources") || title.includes("find sources")) {
      return "Source Discovery Session"
    }
    
    if (title.includes("suggest articles") || title.includes("find content")) {
      return "Content Ideas Session"
    }
    
    if (title.includes("help me find") || title.includes("looking for")) {
      return `${contentLabel} Research`
    }
    
    // Truncate long titles
    return title.length > 45 
      ? `${title.substring(0, 45)}...`
      : title
  }
}